// The caption library: the brand's own words, and the door they come in by.
//
// This screen exists because of a workflow we did not build and cannot stop:
// people draft in ChatGPT or Gemini, paste the results into a spreadsheet, and
// then retype them one at a time into a composer. The material is already
// written; the only thing missing is a door.
//
// Composing stays a separate act. Compose selects from this library and does
// nothing else to it — no importing, no editing, no housekeeping — because the
// moment a publish screen also manages a library, every session becomes two
// jobs at once.
//
// Nothing here is gated on the AI plan, and that is the point rather than an
// oversight: bringing your own material is precisely the workflow of somebody
// who is not paying us for a model.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  deleteDraft, listBrandDetails, listDraftBodies, listDraftsPage, saveDrafts, updateDraft,
} from "../lib/api";
import { logError } from "../lib/logger";
import { useWorkspace } from "../lib/WorkspaceContext";
import {
  MAX_CAPTIONS_PER_IMPORT, isSupportedFile, parseCaptionFile, type ParsedFile,
} from "../lib/sheet";
import { blanksIn, effectiveDetails, resolveTokens, tokensIn } from "../lib/tokens";
import { useBrands } from "../lib/useBrands";
import { BrandDetails } from "../components/BrandDetails";
import { CaptionEditor } from "../components/CaptionEditor";
import { ImportHelp } from "../components/ImportHelp";
import { Loading, Spinner } from "../components/Spinner";
import type { BrandDetail, ContentDraft, DraftKind } from "../lib/types";

const KINDS: Array<{ key: DraftKind; label: string; hint: string }> = [
  { key: "caption", label: "Captions", hint: "The post text itself, on any network." },
  { key: "description", label: "Descriptions", hint: "A line or two under a photo." },
  { key: "punchline", label: "Punch lines", hint: "One sentence that could stand alone." },
  { key: "title", label: "Titles", hint: "Mainly YouTube, which requires one." },
];

const ACCEPT = ".xlsx,.csv,.tsv,.md,.markdown,.txt";
const PAGE_SIZE = 20;

export function Captions() {
  // The brand picker on this screen is the switcher from the nav bar, not a
  // second idea of "which brand". Two controls that disagree about the active
  // workspace is how somebody fills the wrong client's library.
  const { workspaces, active, select, can, ready } = useWorkspace();

  const [kind, setKind] = useState<DraftKind>("caption");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // ---- the table
  const [rowsPage, setRowsPage] = useState<ContentDraft[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  // Applied on a beat's delay: a query per keystroke turns a search box into a
  // load test against somebody's own library.
  const [appliedSearch, setAppliedSearch] = useState("");

  // ---- the brand's own words, and its repeated facts
  //
  // Every row in the workspace, both scopes. `effectiveDetails` narrows them to
  // the brand being looked at, so the preview in the table below is what this
  // caption would actually say if it were published as that brand.
  const [details, setDetails] = useState<BrandDetail[]>([]);
  const [knownBodies, setKnownBodies] = useState<Set<string>>(new Set());

  // ---- the editor
  const [editing, setEditing] = useState<ContentDraft | "new" | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // ---- the importer
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  // Which parsed rows are still going in. Indices into `parsed.captions`, so
  // unticking one is reversible and the original list is never edited.
  const [keep, setKeep] = useState<Set<number>>(new Set());
  const [helpOpen, setHelpOpen] = useState(false);
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Keyed, not boolean: two rows removed in quick succession must not put a
  // spinner on both.
  const [removing, setRemoving] = useState<string | null>(null);

  const fileInput = useRef<HTMLInputElement>(null);

  const workspaceId = active?.workspace_id ?? null;
  const mayWrite = can("post.create");

  // The brands inside this workspace (ADR-0029), and the one chosen in the top
  // bar. Not the same thing as `workspaces` below, which this screen has always
  // called "brand" — that is the billing and library boundary, this is the
  // business the words are for.
  const { brands: workspaceBrands, activeBrandId, activeBrand } = useBrands(workspaceId);

  // Only brands this account may actually add content to. A brand they can see
  // but not write to is not a choice, and offering it only produces a refusal
  // after the file has been read.
  const brands = useMemo(
    () => workspaces.filter((w) => w.permissions?.includes("post.create")),
    [workspaces],
  );

  useEffect(() => {
    const t = setTimeout(() => { setAppliedSearch(search); setPage(0); }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const loadPage = useCallback(async () => {
    if (!workspaceId) return;
    setRowsPage(null);
    try {
      const { rows, total } = await listDraftsPage({
        workspaceId, kind, page, pageSize: PAGE_SIZE, search: appliedSearch,
      });
      setRowsPage(rows);
      setTotal(total);
    } catch (e: any) {
      setError(e.message ?? String(e));
      setRowsPage([]);
    }
  }, [workspaceId, kind, page, appliedSearch]);

  useEffect(() => { void loadPage(); }, [loadPage]);

  const loadBrand = useCallback(async () => {
    if (!workspaceId) return;
    const [d, bodies] = await Promise.all([
      listBrandDetails(workspaceId).catch(() => [] as BrandDetail[]),
      listDraftBodies(workspaceId, kind).catch(() => [] as string[]),
    ]);
    setDetails(d);
    setKnownBodies(new Set(bodies.map((b) => b.trim())));
  }, [workspaceId, kind]);

  useEffect(() => { void loadBrand(); }, [loadBrand]);

  // Switching brand or kind mid-review would save the file into a library the
  // preview was never checked against. Drop it and say so by simply not having
  // it any more.
  useEffect(() => {
    setParsed(null);
    setFileName(null);
    setKeep(new Set());
    setPage(0);
  }, [workspaceId, kind]);

  // What the tokens become for the brand on screen: its own values, falling back
  // to the workspace's shared ones. With no brand chosen only the shared ones
  // apply — a preview must not resolve {{phone}} using whichever business
  // happened to sort first.
  const resolvedDetails = useMemo(
    () => effectiveDetails(details, activeBrandId), [details, activeBrandId],
  );
  const values = useMemo(
    () => new Map(resolvedDetails.map((d) => [d.key, d.value])), [resolvedDetails],
  );

  const parsedRows = useMemo(() => {
    if (!parsed) return [];
    return parsed.captions.map((body, i) => ({
      index: i, body, duplicate: knownBodies.has(body),
    }));
  }, [parsed, knownBodies]);

  const chosen = parsedRows.filter((r) => keep.has(r.index));
  const duplicates = parsedRows.filter((r) => r.duplicate).length;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  async function read(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setNotice(null);
    setParsed(null);
    setKeep(new Set());
    setFileName(file.name);

    if (!isSupportedFile(file.name)) {
      setError(`${file.name} is not a file type we can read. Use .xlsx, .csv, .md or .txt.`);
      return;
    }

    setReading(true);
    try {
      const result = await parseCaptionFile(file);
      setParsed(result);
      // Everything is in by default except what is already here — the common
      // case is "import all of it", and re-importing the same file twice is the
      // mistake worth defaulting against.
      setKeep(new Set(
        result.captions
          .map((body, i) => (knownBodies.has(body) ? -1 : i))
          .filter((i) => i >= 0)
          .slice(0, MAX_CAPTIONS_PER_IMPORT),
      ));
    } catch (err: any) {
      setError(err.message ?? String(err));
      void logError("content_draft", err, {
        step: "parse_file",
        workspaceId,
        // The name and the size, never the contents: this is somebody's
        // unpublished marketing copy.
        context: { extension: file.name.replace(/^.*\./, "").toLowerCase(), bytes: file.size },
      });
    } finally {
      setReading(false);
      // So picking the same file again after a fix still fires a change event.
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function saveImport() {
    if (!workspaceId || chosen.length === 0) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const written = await saveDrafts({
        workspaceId, kind, bodies: chosen.map((r) => r.body), source: "imported",
      });
      setParsed(null);
      setFileName(null);
      setKeep(new Set());
      setPage(0);
      setNotice(
        `Saved ${written.length} ${written.length === 1 ? "caption" : "captions"} to ` +
        `${active?.name ?? "this brand"}. They are in “Use a saved draft” on Compose now.`,
      );
      await Promise.all([loadPage(), loadBrand()]);
    } catch (e: any) {
      setError(e.message ?? String(e));
      void logError("content_draft", e, {
        step: "import_drafts", workspaceId, context: { kind, count: chosen.length },
      });
    } finally {
      setSaving(false);
    }
  }

  /**
   * One handler for both buttons in the editor, because they differ by one
   * word: a copy is an insert, a change is an update, and everything before and
   * after is identical.
   */
  async function saveEdit(body: string, asCopy: boolean) {
    if (!workspaceId) return;
    setSavingEdit(true);
    setError(null);
    setNotice(null);
    try {
      const original = editing === "new" ? null : editing;
      if (asCopy || !original) {
        await saveDrafts({
          workspaceId, kind, bodies: [body.trim()],
          // A copy keeps where the words came from. Fixing a typo in an
          // imported caption does not make it stop having been imported; only a
          // caption written here from nothing is `manual`.
          source: original?.source ?? "manual",
        });
        setNotice(original ? "Saved as a new caption. The original is untouched." : "Saved.");
        setPage(0);
      } else {
        await updateDraft(original.id, body.trim());
        setNotice("Caption updated.");
      }
      setEditing(null);
      await Promise.all([loadPage(), loadBrand()]);
    } catch (e: any) {
      setError(e.message ?? String(e));
      void logError("content_draft", e, {
        step: asCopy ? "copy_draft" : "update_draft", workspaceId, context: { kind },
      });
    } finally {
      setSavingEdit(false);
    }
  }

  async function remove(draft: ContentDraft) {
    setRemoving(draft.id);
    setError(null);
    try {
      await deleteDraft(draft.id);
      await Promise.all([loadPage(), loadBrand()]);
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setRemoving(null);
    }
  }

  const kindLabel = KINDS.find((k) => k.key === kind)!.label.toLowerCase();

  if (!ready) return <Loading label="Loading your brands…" />;

  return (
    <>
      <h1>Caption library</h1>
      <p className="muted lede">
        Write your material wherever you like — ChatGPT, Gemini, a notes app, a spreadsheet —
        and bring it here once. Everything saved to a brand shows up under{" "}
        <strong>Use a saved draft</strong> on <Link to="/compose">Compose</Link>, where the only
        thing left to do is pick one.
      </p>

      <div className="card">
        <h2>Brand</h2>
        {brands.length === 0 ? (
          <p className="muted">
            None of your brands allow you to add content. Ask an owner or manager for a role
            that can create posts.
          </p>
        ) : brands.length === 1 ? (
          <p className="muted">
            Filling the library for <strong>{brands[0].name}</strong>, the only brand you can add
            content to.
          </p>
        ) : (
          <>
            <label htmlFor="brand">Which brand is this material for?</label>
            <select id="brand" className="caption-library-select" value={workspaceId ?? ""}
                    disabled={reading || saving}
                    onChange={(e) => select(e.target.value)}>
              {brands.map((w) => (
                <option key={w.workspace_id} value={w.workspace_id}>{w.name}</option>
              ))}
            </select>
            <p className="muted" style={{ marginTop: ".5rem" }}>
              This is the same choice as the switcher in the bar above — changing it here changes
              which brand the whole app is working on, so nothing is ever filed under a brand you
              were not looking at.
            </p>
          </>
        )}

        <div style={{ height: "1rem" }} />
        <label htmlFor="kind">What is this material?</label>
        <select id="kind" className="caption-library-select caption-library-select-wide" value={kind} disabled={reading || saving}
                onChange={(e) => setKind(e.target.value as DraftKind)}>
          {KINDS.map((k) => (
            <option key={k.key} value={k.key}>{k.label} — {k.hint}</option>
          ))}
        </select>
      </div>

      {workspaceId && (
        <BrandDetails workspaceId={workspaceId} brands={workspaceBrands} details={details}
                      mayWrite={mayWrite} defaultBrandId={activeBrandId}
                      onChanged={loadBrand} />
      )}

      {mayWrite && (
        <div className="card">
          <div className="card-head head-tight">
            <h2>Import a file</h2>
            {/* A question mark and not a paragraph. The format is obvious once
                you have seen one file and unobvious before that, which is a
                problem an example solves and more prose does not. */}
            <button type="button" className="help-icon" onClick={() => setHelpOpen(true)}
                    aria-label="Where the file comes from — samples and a prompt for your model"
                    title="Sample files, and a prompt to paste into ChatGPT or Gemini">
              ?
            </button>
          </div>
          <p className="muted card-sub">
            A spreadsheet with <strong>one caption per row in the first column</strong>, or a
            markdown or text file with one caption per block. Everything after the first column is
            ignored, so notes to yourself can stay in the sheet.
          </p>
          <div className="row media-source">
            <input ref={fileInput} type="file" accept={ACCEPT} onChange={read}
                   className="visually-hidden" tabIndex={-1} />
            <button onClick={() => fileInput.current?.click()} disabled={reading || saving}>
              {reading && <Spinner />} {reading ? "Reading…" : "Choose a file"}
            </button>
            <span className="muted">.xlsx · .csv · .md · .txt</span>
            <span className="spacer" />
            <button onClick={() => setEditing("new")} disabled={reading || saving}>
              Write one here
            </button>
          </div>
        </div>
      )}

      {parsed && (
        <div className="card">
          <h2>What we read from {fileName}</h2>
          <p className="muted">Read {parsed.from}.</p>

          <ul className="parse-tally">
            <li><strong>{parsedRows.length}</strong> captions found</li>
            {parsed.headerSkipped && <li>heading row “{parsed.headerSkipped}” skipped</li>}
            {parsed.blank > 0 && <li>{parsed.blank} blank rows skipped</li>}
            {parsed.tooLong.length > 0 && (
              <li className="warn-item">
                {parsed.tooLong.length} too long to store (over 10,000 characters)
              </li>
            )}
            {duplicates > 0 && (
              <li>{duplicates} already in {active?.name ?? "this brand"}’s library — unticked</li>
            )}
            {parsedRows.length > MAX_CAPTIONS_PER_IMPORT && (
              <li className="warn-item">
                only the first {MAX_CAPTIONS_PER_IMPORT} are ticked — import the rest in a second
                file
              </li>
            )}
          </ul>

          {parsedRows.length === 0 ? (
            <p className="notice">
              Nothing readable came out of that file. If it is a spreadsheet, check the captions
              are in the first column of the first sheet; if it is a document, check there is a
              blank line between them.
            </p>
          ) : (
            <>
              <div className="row parse-actions">
                <button type="button" className="linkish"
                        onClick={() => setKeep(new Set(parsedRows.map((r) => r.index)
                          .slice(0, MAX_CAPTIONS_PER_IMPORT)))}>
                  Tick all
                </button>
                <button type="button" className="linkish" onClick={() => setKeep(new Set())}>
                  Untick all
                </button>
              </div>
              <ul className="parse-list">
                {parsedRows.map((r) => (
                  <li key={r.index} className={r.duplicate ? "is-duplicate" : undefined}>
                    <label>
                      <input
                        type="checkbox"
                        checked={keep.has(r.index)}
                        // The cap is enforced where it is reached rather than at
                        // save time, so nobody ticks six hundred rows and then
                        // reads that a hundred of them were refused.
                        disabled={!keep.has(r.index) && keep.size >= MAX_CAPTIONS_PER_IMPORT}
                        onChange={(e) => {
                          const next = new Set(keep);
                          e.target.checked ? next.add(r.index) : next.delete(r.index);
                          setKeep(next);
                        }}
                      />
                      <span className="parse-body">{r.body}</span>
                    </label>
                    <span className="muted parse-meta">
                      {r.duplicate ? "already saved" : `${r.body.length} characters`}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="row modal-actions">
            <button className="primary" onClick={saveImport}
                    disabled={saving || chosen.length === 0 || !mayWrite}>
              {saving && <Spinner />}{" "}
              {saving ? "Saving…"
                : `Save ${chosen.length} ${chosen.length === 1 ? "caption" : "captions"}`}
            </button>
            <button onClick={() => { setParsed(null); setFileName(null); setKeep(new Set()); }}
                    disabled={saving}>
              Discard
            </button>
          </div>
        </div>
      )}

      {notice && <div className="card"><div className="notice ok-note">{notice}</div></div>}
      {error && <div className="card err">{error}</div>}

      <div className="card">
        <div className="field-head">
          <h2 style={{ margin: 0 }}>
            Saved {kindLabel}{total > 0 ? ` (${total})` : ""}
          </h2>
          <span className="spacer" />
          <label className="visually-hidden" htmlFor="caption-search">Search captions</label>
          <input id="caption-search" type="search" className="caption-search" value={search}
                 placeholder="Search…" onChange={(e) => setSearch(e.target.value)} />
        </div>

        {!rowsPage ? (
          <Loading label={`Loading saved ${kindLabel}…`} />
        ) : rowsPage.length === 0 ? (
          <p className="muted" style={{ marginTop: ".8rem" }}>
            {appliedSearch
              ? `Nothing here matches “${appliedSearch}”.`
              : <>
                  Nothing saved for {active?.name ?? "this brand"} yet. Import a file above, write
                  one here, or use the <Link to="/studio">studio</Link>.
                </>}
          </p>
        ) : (
          <>
            <div className="table-scroll">
              <table className="data-table caption-table">
                <thead>
                  <tr>
                    <th>Caption</th>
                    <th>Uses</th>
                    <th>Source</th>
                    <th>Added</th>
                    <th className="col-actions"><span className="visually-hidden">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {rowsPage.map((d) => {
                    const used = tokensIn(d.body);
                    const blanks = blanksIn(d.body).length;
                    const preview = resolveTokens(d.body, values).text;
                    return (
                      <tr key={d.id}>
                        {/* The resolved text, because that is what publishing
                            this row would actually put on a page. The raw form
                            is one click away in the editor. */}
                        <td className="caption-cell" title={preview}>{preview}</td>
                        <td className="caption-uses">
                          {used.length === 0 && blanks === 0 && <span className="muted">—</span>}
                          {used.map((k) => (
                            <code key={k} className={values.has(k) ? "token" : "token token-missing"}>
                              {`{{${k}}}`}
                            </code>
                          ))}
                          {blanks > 0 && (
                            <span className="muted blanks-note">{blanks} to write</span>
                          )}
                        </td>
                        <td className="muted">
                          {d.source === "ai" ? "AI" : d.source === "imported" ? "Imported" : "By hand"}
                        </td>
                        <td className="muted">{new Date(d.created_at).toLocaleDateString()}</td>
                        <td className="col-actions">
                          {mayWrite ? (
                            <>
                              <button className="linkish" onClick={() => setEditing(d)}
                                      title="Change it, or save your changes as a new copy">
                                Edit
                              </button>
                              <button className="linkish danger-link" onClick={() => remove(d)}
                                      disabled={removing === d.id}>
                                {removing === d.id ? <Spinner label="Removing" /> : "Delete"}
                              </button>
                            </>
                          ) : <span className="muted">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {pages > 1 && (
              <div className="row pager">
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                  Previous
                </button>
                <span className="muted">
                  Page {page + 1} of {pages} · {total.toLocaleString()} saved
                </span>
                <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
                        disabled={page >= pages - 1}>
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {helpOpen && (
        <ImportHelp
          kind={kind}
          brandName={activeBrand?.name ?? active?.name ?? "this brand"}
          details={resolvedDetails}
          onClose={() => setHelpOpen(false)}
        />
      )}

      {editing && (
        <CaptionEditor
          draft={editing === "new" ? null : editing}
          details={resolvedDetails}
          busy={savingEdit}
          onSave={saveEdit}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}
